import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faChevronRight, faHourglassHalf } from '@fortawesome/pro-solid-svg-icons';
import { IonCol, IonContent, IonGrid, IonItem, IonLabel, IonPage, IonRow } from '@ionic/react';
import { each, entries, filter, find, flow, groupBy, keys, map, sumBy, toInteger } from 'lodash/fp';
import { useState } from 'react';
import { CircleIcon } from '../../components/Icon';
import { useEmployees, useLeaves } from '../../hooks/useSwr';
import { Leave, OdooEmployee, OdooLeave } from '../../types/models';
import { LeaveState } from '../../utils/odooUtils';
import { BottomSlotWarning } from '../../App';
import { Header } from '../../components/Header';
import {
    faBuildingCircleCheck,
    faBuildingCircleXmark,
    faPersonCircleCheck,
    faPersonCircleXmark,
    faBan,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IosHeader } from '../../components/IosHeader';

export type DisplayMatrixItemType = {
    title: string;
    icon: IconProp;
    hasSum?: boolean;
    showCount?: boolean;
    style?: React.CSSProperties;
    shrink?: number;
};

export const displayMatrix: { [key in LeaveState]: DisplayMatrixItemType } = {
    [LeaveState.WaitValidation]: {
        title: 'Congés à valider',
        icon: faHourglassHalf,
        hasSum: true,
        showCount: true,
    },
    [LeaveState.AdherentValidation]: {
        title: "Congés validés par l'adhérent",
        icon: faPersonCircleCheck,
        hasSum: true,
        style: {
            '--fa-secondary-color': 'white',
            '--fa-secondary-opacity': '1',
            '--fa-primary-color': 'green',
        } as React.CSSProperties,
        shrink: 5,
    },
    [LeaveState.GeiqValidation]: {
        title: 'Congés validés par le GEIQ',
        icon: faBuildingCircleCheck,
        hasSum: true,
        style: {
            '--fa-secondary-color': 'white',
            '--fa-secondary-opacity': '1',
            '--fa-primary-color': 'green',
        } as React.CSSProperties,
        shrink: 7,
    },
    [LeaveState.AdherentRefusal]: {
        title: "Congés refusés par l'adhérent",
        icon: faPersonCircleXmark,
        hasSum: true,
        style: {
            '--fa-secondary-color': 'white',
            '--fa-secondary-opacity': '1',
            '--fa-primary-color': 'red',
        } as React.CSSProperties,
        shrink: 5,
    },
    [LeaveState.GeiqRefusal]: {
        title: 'Congés refusés par le GEIQ',
        icon: faBuildingCircleXmark,
        hasSum: true,
        style: {
            '--fa-secondary-color': 'white',
            '--fa-secondary-opacity': '1',
            '--fa-primary-color': 'red',
        } as React.CSSProperties,
        shrink: 7,
    },
    [LeaveState.Canceled]: {
        title: 'Congés annulés',
        icon: faBan,
        hasSum: true,
        style: {
            '--fa-secondary-color': 'white',
            '--fa-secondary-opacity': '1',
        } as React.CSSProperties,
        shrink: 7,
    },
};
export const ChooseLeaveState: React.FC = () => {
    const { data: employees } = useEmployees();
    const { standardizedOdooLeaves: leaves } = useLeaves();
    const [seachText, setSearchText] = useState<string>('');

    const aggregateEmployeeData = (leaveByEmployee: { [employeeId: number]: OdooLeave[] }) =>
        map((employeeId: string) => ({
            ...find({ id: toInteger(employeeId) })(employees),
            count: leaveByEmployee[toInteger(employeeId)].length,
        }))(keys(leaveByEmployee));

    const sumOfEmployeesCount = (leaveState: LeaveState) => sumBy('count')(leavesEntriesByState[leaveState]);

    const filteredEmployees = filter((employee: OdooEmployee) =>
        employee.name.toLowerCase().includes(seachText.toLowerCase())
    )(employees);

    const filteredLeaves = filter((leave: Leave) =>
        filteredEmployees.some((employee) => employee.id === leave.employee_id)
    )(leaves);

    let leavesEntriesByState = groupBy('state')(filteredLeaves);

    each((state: string) => {
        leavesEntriesByState[state] = flow(groupBy('employee_id'), aggregateEmployeeData)(leavesEntriesByState[state]);
    }, keys(leavesEntriesByState));

    return (
        <IonPage>
            <IosHeader title="Congés" />
            <IonContent>
                <Header title="Congés" />
                <div className="main-content-d pt-4">
                    <div className="content-validation-d left-offset-10">
                        {map(
                            ([state, data]: [LeaveState, DisplayMatrixItemType]) => (
                                <IonItem
                                    slot="header"
                                    className="ion-no-padding font-bold text-lg bg-white mb-4"
                                    key={state}
                                    href={(sumOfEmployeesCount(state) && `/validationconges/${state}`) || undefined}
                                    detail={false}
                                >
                                    <div className="ml-2">
                                        <CircleIcon
                                            icon={data.icon}
                                            size="text-3xl"
                                            style={data.style}
                                            shrink={data.shrink}
                                        />
                                    </div>
                                    <IonLabel className="ml-4">{data.title}</IonLabel>
                                    <div slot="end">
                                        <IonGrid>
                                            <IonRow>
                                                <IonCol>
                                                    {data.hasSum && (
                                                        <IonLabel color="tertiary">
                                                            {sumOfEmployeesCount(state)}
                                                        </IonLabel>
                                                    )}
                                                </IonCol>
                                                <IonCol>
                                                    <FontAwesomeIcon
                                                        icon={faChevronRight}
                                                        opacity={(sumOfEmployeesCount(state) && 1) || 0.25}
                                                    />
                                                </IonCol>
                                            </IonRow>
                                        </IonGrid>
                                    </div>
                                </IonItem>
                            ),
                            entries(displayMatrix)
                        )}
                    </div>
                </div>
            </IonContent>
            <BottomSlotWarning />
        </IonPage>
    );
};
